import type { PaginationMeta } from '@eliadmin/core/src/api/api-types';
import { fetchData } from '@eliadmin/core/src/api/fetcher';
import {
  buildStoreListRequest,
  storeApiResources,
} from '@eliadmin/core/src/api/store-list-request';
import type {
  ProductCategorySummary,
  ProductIndexData,
  ProductVariantValue,
} from '@eliadmin/core/src/api/products/types/product-types';

export type FitmentVariantValue = Partial<ProductVariantValue> & {
  external_id?: string | null;
  attribute?: {
    id?: number;
    external_id?: string | null;
    name?: string | null;
  } | null;
};

export type FitmentVariant = {
  id: number;
  name?: string | null;
  code?: string | null;
  ext_code?: string | null;
  values?: FitmentVariantValue[] | null;
};

export type FitmentProduct = ProductIndexData & {
  variant_fitments_differ?: boolean;
  fitment_variants?: FitmentVariant[] | null;
};

export type FitmentProductsResponse = {
  data: FitmentProduct[];
  meta: PaginationMeta;
};

type RequestContext = {
  locale: string;
  signal?: AbortSignal;
};

export type FitmentTargetsQuery = {
  sourceProductId?: number;
  sourceVariantId?: number;
  categoryIds?: number[];
  manufacturerIds?: number[];
  search?: string;
  results?: number;
  page?: number;
  includeVariants?: boolean;
};

export type FitmentSourcesQuery = {
  targetProductId: number;
  targetVariantId?: number;
  categoryIds?: number[];
  includeCategories?: boolean;
  search?: string;
  results?: number;
  page?: number;
  includeVariants?: boolean;
};

export type MotorcyclesQuery = {
  categoryId: number;
  search?: string;
  results?: number;
  page?: number;
};

export function buildFitmentTargetsQuery(query: FitmentTargetsQuery) {
  return {
    source_product_id: query.sourceProductId,
    source_variant_id: query.sourceVariantId,
    where:
      query.categoryIds?.length || query.manufacturerIds?.length
        ? {
            ...(query.categoryIds?.length ? { categories: { in: query.categoryIds } } : {}),
            ...(query.manufacturerIds?.length
              ? { manufacturers: { in: query.manufacturerIds } }
              : {}),
          }
        : undefined,
    search: query.search,
    results: query.results,
    page: query.page,
    include_variants: query.includeVariants,
  };
}

export function buildFitmentSourcesQuery(query: FitmentSourcesQuery) {
  return {
    target_product_id: query.targetProductId,
    target_variant_id: query.targetVariantId,
    where: query.categoryIds?.length ? { categories: { in: query.categoryIds } } : undefined,
    categories: query.includeCategories,
    search: query.search,
    results: query.results,
    page: query.page,
    include_variants: query.includeVariants,
  };
}

export function buildMotorcyclesQuery(query: MotorcyclesQuery) {
  return {
    where: { categoryIds: { in: [query.categoryId] } },
    search: query.search,
    results: query.results,
    page: query.page,
  };
}

/** Fetch every root page for tree navigation; sources expands selected ancestors server-side. */
export async function fetchFitmentCategoryTree({ locale, signal }: RequestContext) {
  const maxRootPages = 20;
  const categories: ProductCategorySummary[] = [];
  let lastPage = 1;

  for (let page = 1; page <= lastPage; page += 1) {
    signal?.throwIfAborted();
    const response = await fetchData<{
      data: ProductCategorySummary[];
      meta?: { last_page?: unknown };
    }>({
      ...buildStoreListRequest(storeApiResources.storeCategories, {
        where: { parent: { exists: false } },
        query: { childs: true, results: 100, page },
      }),
      locale,
      signal,
    });
    signal?.throwIfAborted();

    const pageData = response?.data;
    const reportedLastPage = response?.meta?.last_page;
    if (
      !Array.isArray(pageData) ||
      typeof reportedLastPage !== 'number' ||
      !Number.isSafeInteger(reportedLastPage) ||
      reportedLastPage < 1
    ) {
      throw new Error('API kategórií vrátilo neplatné údaje alebo stránkovanie.');
    }
    if (reportedLastPage > maxRootPages) {
      throw new Error(`Zoznam kategórií presahuje bezpečný limit ${maxRootPages} strán.`);
    }
    if (page === 1) lastPage = reportedLastPage;
    else if (reportedLastPage !== lastPage) {
      throw new Error('Stránkovanie kategórií sa počas načítania zmenilo. Skúste to znova.');
    }
    categories.push(...pageData);
  }

  return { data: categories };
}

/** All motorcycles imported into the technical RS Moto category. */
export function fetchRSMotoMotorcycles({
  locale,
  signal,
  query,
}: RequestContext & { query: MotorcyclesQuery }) {
  const { where, ...params } = buildMotorcyclesQuery(query);
  return fetchData<FitmentProductsResponse>({
    ...buildStoreListRequest(storeApiResources.storeProducts, { where, query: params }),
    locale,
    signal,
  });
}

/** Products on which a source product/variant fits, or selectable motorcycle targets. */
export function fetchFitmentTargets({
  locale,
  signal,
  query,
}: RequestContext & { query: FitmentTargetsQuery }) {
  const { where, ...params } = buildFitmentTargetsQuery(query);
  return fetchData<FitmentProductsResponse>({
    ...buildStoreListRequest(storeApiResources.storeProductFitmentsTargets, {
      where,
      query: params,
    }),
    locale,
    signal,
  });
}

/** Source parts compatible with the selected motorcycle product/variant. */
export function fetchFitmentSources({
  locale,
  signal,
  query,
}: RequestContext & { query: FitmentSourcesQuery }) {
  const { where, ...params } = buildFitmentSourcesQuery(query);
  return fetchData<FitmentProductsResponse>({
    ...buildStoreListRequest(storeApiResources.storeProductFitmentsSources, {
      where,
      query: params,
    }),
    locale,
    signal,
  });
}
